import { z } from 'zod';
import {
  backorderRowSchema,
  type BackorderRow,
  type CreateBackorderBatchInput,
  type CreateBackorderInput,
} from '@eombor/contracts';
import type { ApiClient } from '../../../shared/api/client.js';
import { apiClient } from '../../auth/model/auth.store.js';

/**
 * BILDIRGI — API yuzasi.
 *
 * Ro'yxat UCH manzilda va bu backenddagi bilan ayni sabab: xodimning O'Z
 * arizalari, skladchi-rahbar NAVBATI va biriktirishga TAYYOR bo'lganlari
 * uch xil savolga javob beradi. Bitta manzilga bayroq qo'yilsa, bayroqni
 * unutgan chaqiruv buyurtmachiga hamkasblarining arizalarini ochib
 * qo'yardi — uning doirasi butun korxona bo'lishi mumkin.
 */

export const backorderPageSchema = z.object({
  items: z.array(backorderRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type BackorderPage = z.infer<typeof backorderPageSchema>;

const countSchema = z.object({ count: z.number() });

export interface FulfilItem {
  backorderId: string;
  batchId: string;
  cellId: string;
  qty: number;
}

export interface BackorderQuery {
  status?: string;
  warehouseId?: string;
  page?: number;
  limit?: number;
}

export interface BackorderApi {
  /** Xodimning O'Z bildirgilari. */
  listMine: (query?: BackorderQuery) => Promise<BackorderPage>;
  /** Skladchi va rahbar navbati — doiralangan. */
  listQueue: (query?: BackorderQuery) => Promise<BackorderPage>;
  /** Qabul qilingan, biriktirilishi kutilayotganlari. */
  listReady: (query?: BackorderQuery) => Promise<BackorderPage>;
  /** «Kutilmoqda» bo'limining soni — ro'yxatning o'zi so'ralmasdan. */
  readyCount: () => Promise<number>;
  /** Ko'rib chiqilmagan bildirgilar soni — skladchining qizil belgisi. */
  pendingCount: () => Promise<number>;
  get: (id: string) => Promise<BackorderRow>;
  create: (input: CreateBackorderInput) => Promise<BackorderRow>;
  /** Savat — har mahsulot alohida bildirgi, hammasi bitta tranzaksiyada. */
  createBatch: (input: CreateBackorderBatchInput) => Promise<BackorderRow[]>;
  accept: (id: string, nomenclatureId: string | null) => Promise<BackorderRow>;
  reject: (id: string, reason: string) => Promise<BackorderRow>;
  cancel: (id: string) => Promise<BackorderRow>;
  /**
   * BIR NECHTA BILDIRGI — BITTA talabnomaga.
   *
   * Yakka biriktirish ham SHU manzildan o'tadi: ikki yo'l bo'lsa,
   * ulardan biri bir kun ikkinchisidan boshqacha ishlab qolardi.
   */
  fulfilMany: (
    items: readonly FulfilItem[],
  ) => Promise<BackorderRow[]>;
}

const queryString = (query: BackorderQuery): string => {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set('status', query.status);
  if (query.warehouseId !== undefined) params.set('warehouseId', query.warehouseId);
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? 20));
  return `?${params.toString()}`;
};

const action = (client: ApiClient, id: string, name: string, body?: unknown) =>
  client.apiFetch(`/backorders/${id}/${name}`, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
    schema: backorderRowSchema,
  });

export const createBackorderApi = (client: ApiClient): BackorderApi => ({
  listMine: (query = {}) =>
    client.apiFetch(`/backorders/mine${queryString(query)}`, { schema: backorderPageSchema }),

  listQueue: (query = {}) =>
    client.apiFetch(`/backorders${queryString(query)}`, { schema: backorderPageSchema }),

  listReady: (query = {}) =>
    client.apiFetch(`/backorders/ready${queryString(query)}`, { schema: backorderPageSchema }),

  readyCount: async () =>
    (await client.apiFetch('/backorders/ready/count', { schema: countSchema })).count,

  pendingCount: async () =>
    (await client.apiFetch('/backorders/pending/count', { schema: countSchema })).count,

  get: (id) => client.apiFetch(`/backorders/${id}`, { schema: backorderRowSchema }),

  create: (input) =>
    client.apiFetch('/backorders', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: backorderRowSchema,
    }),

  createBatch: (input) =>
    client.apiFetch('/backorders/batch', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: z.array(backorderRowSchema),
    }),

  /* `nomenclatureId` `null` bo'lsa ham YUBORILADI: katalogdan tanlab
     yozilgan bildirgida u allaqachon bor va server o'zinikini ishlatadi. */
  accept: (id, nomenclatureId) => action(client, id, 'accept', { nomenclatureId }),
  reject: (id, reason) => action(client, id, 'reject', { reason }),
  cancel: (id) => action(client, id, 'cancel'),
  fulfilMany: (items) =>
    client.apiFetch('/backorders/fulfil', {
      method: 'POST',
      body: JSON.stringify({ items }),
      schema: z.array(backorderRowSchema),
    }),
});

export const backorderApi = createBackorderApi(apiClient);
export type { BackorderRow };
